import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useCreateStadt, useUpdateStadt } from '../../api/stadt';
import type { Stadt } from '../../types/stadt';

interface StadtModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (stadt: Stadt) => void;
  editingStadt?: Stadt;
  initialName?: string;
}

export function StadtModal({
  isOpen,
  onClose,
  onSuccess,
  editingStadt,
  initialName = '',
}: StadtModalProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const createMutation = useCreateStadt();
  const updateMutation = useUpdateStadt();

  const isEditMode = !!editingStadt;
  const isPending = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (isOpen) {
      setName(editingStadt?.name || initialName);
      setError(null);
      // Focus input after modal opens
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, editingStadt, initialName]);

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name ist erforderlich');
      return;
    }

    setError(null);

    try {
      let result: Stadt;

      if (isEditMode) {
        const response = await updateMutation.mutateAsync({
          id: editingStadt.id,
          data: { name: trimmedName },
        });
        result = response.data;
        toast.success('Stadt erfolgreich aktualisiert');
      } else {
        const response = await createMutation.mutateAsync({ name: trimmedName });
        result = response.data;
        toast.success('Stadt erfolgreich erstellt');
      }

      onSuccess(result);
      onClose();
    } catch (err: unknown) {
      const errorMessage = isEditMode
        ? 'Fehler beim Aktualisieren der Stadt'
        : 'Fehler beim Erstellen der Stadt';

      // Check for duplicate name error
      if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as { response?: { data?: { message?: string } } }).response;
        if (response?.data?.message?.includes('already exists') ||
            response?.data?.message?.includes('existiert bereits')) {
          setError('Eine Stadt mit diesem Namen existiert bereits');
          toast.error('Eine Stadt mit diesem Namen existiert bereits');
          return;
        }
      }

      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Failed to save stadt:', err);
    }
  };

  const handleClose = () => {
    setName('');
    setError(null);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal - using div instead of form to avoid nested forms */}
        <div className="relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-md sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {isEditMode ? 'Stadt bearbeiten' : 'Neue Stadt erstellen'}
                </h3>

                {error && (
                  <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <div className="mt-4">
                  <label htmlFor="stadtName" className="block text-sm font-medium text-gray-700">
                    Name *
                  </label>
                  <input
                    ref={inputRef}
                    type="text"
                    id="stadtName"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="z.B. Berlin, Hamburg..."
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isPending}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
            >
              {isPending
                ? (isEditMode ? 'Wird gespeichert...' : 'Wird erstellt...')
                : (isEditMode ? 'Speichern' : 'Erstellen')}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Abbrechen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
